import React, { useEffect, useState } from "react";
import { message } from "antd";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Fade,
  Slide,
  Switch,
  Autocomplete,
} from "@mui/material";
import imageCompression from "browser-image-compression";

const API_BASE_URL = "https://uktomars.co.uk/api";
const getTextValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") return value.en || value.ku || value.ar || "";
  return String(value);
};
const flattenCategoryTree = (nodes = [], depth = 0) => {
  const list = [];
  nodes.forEach((node) => {
    const currentName = getTextValue(node?.name);

    const children = Array.isArray(node?.children)
      ? node.children
      : Array.isArray(node?.childrens)
        ? node.childrens
        : [];
    const isParent = children.length > 0;
    list.push({
      id: node.id,
      label: currentName,
      depth,
      selectable: !isParent,
      filters: Array.isArray(node?.filters) ? node.filters : [],
    });

    if (children.length > 0) {
      list.push(...flattenCategoryTree(children, depth + 1));
    }
  });
  return list;
};

const PostProduct = () => {
  const [formData, setFormData] = useState({
    name: "",
    price: "",
    unit: "",
    case_size: "",
    layer: "",
    pallet: "",
    unit_barcode: "",
    case_barcode: "",
    weight: "",
    vat: "",
    units: "",
    tariff: "",
    origin: "",
    image: "",
    brand_id: "",
    category_id: "",
    filter_ids: [],
    is_active: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");

  useEffect(() => {
    const getBrands = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/admin/brands`);
        if (!res.ok) {
          throw new Error(`HTTP error! Status: ${res.status}`);
        }
        const responseData = await res.json();
        const brandsData = Array.isArray(responseData?.data)
          ? responseData.data
          : Array.isArray(responseData)
            ? responseData
            : [];
        setBrands(brandsData);
      } catch (error) {
        console.error("Error fetching brands:", error);
        message.error("Failed to load brands");
      }
    };
    const getCategories = async () => {
      try {
        let categoriesData = [];
        const categoryEndpoints = ["/admin/categories", "/categories"];

        for (const endpoint of categoryEndpoints) {
          const res = await fetch(`${API_BASE_URL}${endpoint}`);
          if (!res.ok) continue;
          const responseData = await res.json();
          const parsed = Array.isArray(responseData?.data)
            ? responseData.data
            : Array.isArray(responseData)
              ? responseData
              : [];
          if (parsed.length > 0) {
            categoriesData = parsed;
            break;
          }
        }

        setCategories(flattenCategoryTree(categoriesData));
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    getBrands();
    getCategories();
  }, []);

  const handleCategoryChange = (selectedCategory) => {
    const categoryId = selectedCategory?.id ?? "";
    const categoryFilters = selectedCategory?.filters || [];
    setFilters(categoryFilters);
    setFormData((prev) => ({
      ...prev,
      category_id: categoryId,
      filter_ids: [],
    }));
  };

  const handleChange = (key) => (e) => {
    setFormData((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleImageUpload = async (file) => {
    const localPreview = URL.createObjectURL(file);
    setPreviewUrl(localPreview);
    setUploading(true);

    try {
      const compressedFile = await imageCompression(file, {
        maxSizeMB: 1,
        maxWidthOrHeight: 512,
        useWebWorker: true,
        initialQuality: 0.85,
        alwaysKeepResolution: true,
      });

      const uploadFormData = new FormData();
      uploadFormData.append("image", compressedFile);

      const uploadRes = await fetch(`${API_BASE_URL}/admin/images/upload`, {
        method: "POST",
        body: uploadFormData,
      });
      if (!uploadRes.ok) {
        throw new Error(`Upload failed. Status: ${uploadRes.status}`);
      }

      const uploadData = await uploadRes.json();
      const uploadedPath =
        uploadData?.image_path ||
        uploadData?.path ||
        uploadData?.data?.path ||
        uploadData?.url ||
        "";
      if (!uploadedPath) {
        throw new Error("Upload response missing file path");
      }

      setFormData((prev) => ({ ...prev, image: uploadedPath }));
      if (uploadData?.url) setPreviewUrl(uploadData.url);
      message.success("Image uploaded");
    } catch (error) {
      console.error("Image compression/upload failed:", error);
      message.error("Failed to upload image");
      setFormData((prev) => ({ ...prev, image: "" }));
    } finally {
      URL.revokeObjectURL(localPreview);
      setUploading(false);
    }
  };

  const onChangeUpload = (e) => {
    if (e?.target?.files?.[0]) handleImageUpload(e.target.files[0]);
  };

  const handleReset = () => {
    setFormData({
      name: "",
      price: "",
      unit: "",
      case_size: "",
      layer: "",
      pallet: "",
      unit_barcode: "",
      case_barcode: "",
      weight: "",
      vat: "",
      units: "",
      tariff: "",
      origin: "",
      image: "",
      brand_id: "",
      category_id: "",
      filter_ids: [],
      is_active: true,
    });
    setPreviewUrl("");
    setFilters([]);
  };

  const selectedBrandOption =
    formData.brand_id === "" || formData.brand_id === null
      ? null
      : brands.find((b) => String(b.id) === String(formData.brand_id)) || null;

  const selectedCategoryOption =
    formData.category_id === "" || formData.category_id === null
      ? null
      : categories.find(
          (c) => String(c.id) === String(formData.category_id),
        ) || null;

  const selectedFilterOptions = (formData.filter_ids || [])
    .map((id) => filters.find((f) => String(f.id) === String(id)) || null)
    .filter(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      message.warning("Please enter product name");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        name: formData.name.trim(),
        price: formData.price,
        unit: formData.unit || null,
        case_size: formData.case_size || null,
        layer: formData.layer ? Number(formData.layer) : null,
        pallet: formData.pallet ? Number(formData.pallet) : null,
        unit_barcode: formData.unit_barcode || null,
        case_barcode: formData.case_barcode || null,
        weight: formData.weight || null,
        vat: formData.vat || null,
        units: formData.units || null,
        tariff: formData.tariff || null,
        origin: formData.origin || null,
        image: formData.image || null,
        brand_id: formData.brand_id ? Number(formData.brand_id) : null,
        category_id: formData.category_id ? Number(formData.category_id) : null,
        filter_ids: (formData.filter_ids || []).map((id) => Number(id)),
        is_active: formData.is_active,
      };

      const res = await fetch(`${API_BASE_URL}/admin/products`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        throw new Error(`HTTP error! Status: ${res.status}`);
      }

      message.success("Product created successfully");
      window.location.href = "/admin/products";
    } catch (error) {
      console.error("Error creating product:", error);
      message.error("Failed to create product");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Fade in timeout={500}>
      <Box sx={{ maxWidth: 1000, mx: "auto", my: 5, px: 2, marginBottom: 15 }}>
        <Slide direction="up" in timeout={400}>
          <Paper elevation={6} sx={{ p: { xs: 2, md: 4 }, borderRadius: 3 }}>
            <Box component="form" onSubmit={handleSubmit}>
              <Box sx={{ textAlign: "center", mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Create Product
                </Typography>
              </Box>

              <Box
                sx={{
                  mb: 4,
                  p: 2,
                  border: "1px dashed",
                  borderColor: "divider",
                  borderRadius: 2,
                  textAlign: "center",
                  backgroundColor: "grey.50",
                }}
              >
                <Box
                  sx={{
                    width: 180,
                    height: 180,
                    mx: "auto",
                    mb: 2,
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "divider",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "common.white",
                  }}
                >
                  {previewUrl || formData.image ? (
                    <Box
                      component="img"
                      src={previewUrl || formData.image}
                      alt="Product preview"
                      sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No image
                    </Typography>
                  )}
                </Box>
                <input
                  id="product-image-upload"
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={onChangeUpload}
                />
                <label htmlFor="product-image-upload">
                  <Button variant="outlined" component="span">
                    {uploading ? "Uploading..." : "Upload Product Image"}
                  </Button>
                </label>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Name"
                    value={formData.name}
                    onChange={handleChange("name")}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Price"
                    value={formData.price}
                    onChange={handleChange("price")}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Unit"
                    value={formData.unit}
                    onChange={handleChange("unit")}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Case Size"
                    value={formData.case_size}
                    onChange={handleChange("case_size")}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Layer"
                    value={formData.layer}
                    onChange={handleChange("layer")}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Pallet"
                    value={formData.pallet}
                    onChange={handleChange("pallet")}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Unit Barcode"
                    value={formData.unit_barcode}
                    onChange={handleChange("unit_barcode")}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Case Barcode"
                    value={formData.case_barcode}
                    onChange={handleChange("case_barcode")}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Weight"
                    value={formData.weight}
                    onChange={handleChange("weight")}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="VAT"
                    value={formData.vat}
                    onChange={handleChange("vat")}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Units"
                    value={formData.units}
                    onChange={handleChange("units")}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Tariff"
                    value={formData.tariff}
                    onChange={handleChange("tariff")}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Origin"
                    value={formData.origin}
                    onChange={handleChange("origin")}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={brands}
                    value={selectedBrandOption}
                    onChange={(event, newValue) => {
                      setFormData((prev) => ({
                        ...prev,
                        brand_id: newValue?.id ?? "",
                      }));
                    }}
                    getOptionLabel={(option) => option?.name || ""}
                    isOptionEqualToValue={(option, value) =>
                      String(option.id) === String(value.id)
                    }
                    renderOption={(props, option) => (
                      <Box
                        component="li"
                        {...props}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        {option.image_url && (
                          <Box
                            component="img"
                            src={option.image_url}
                            alt={option.name}
                            sx={{
                              width: 28,
                              height: 28,
                              objectFit: "contain",
                              borderRadius: 1,
                              border: "1px solid",
                              borderColor: "divider",
                              flexShrink: 0,
                            }}
                          />
                        )}
                        {option.name}
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField {...params} label="Brand" />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={categories}
                    value={selectedCategoryOption}
                    onChange={(event, newValue) => handleCategoryChange(newValue)}
                    getOptionLabel={(option) => option?.label || ""}
                    getOptionDisabled={(option) => !option.selectable}
                    isOptionEqualToValue={(option, value) =>
                      String(option.id) === String(value.id)
                    }
                    renderOption={(props, option) => (
                      <Box
                        component="li"
                        {...props}
                        sx={{
                          pl: option.depth * 2,
                          fontWeight: option.selectable ? 400 : 700,
                          opacity: option.selectable ? 1 : 0.65,
                        }}
                      >
                        {option.label}
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField {...params} label="Category" />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    multiple
                    options={filters}
                    value={selectedFilterOptions}
                    onChange={(event, newValues) => {
                      setFormData((prev) => ({
                        ...prev,
                        filter_ids: newValues.map((v) => v.id),
                      }));
                    }}
                    getOptionLabel={(option) => option?.name || ""}
                    isOptionEqualToValue={(option, value) =>
                      String(option.id) === String(value.id)
                    }
                    disableCloseOnSelect
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        {option.name}
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField {...params} label="Filters" />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Active
                  </Typography>
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        is_active: e.target.checked,
                      }))
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{ display: "flex", gap: 2, justifyContent: "center" }}
                  >
                    <Button variant="outlined" onClick={handleReset}>
                      Reset
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => (window.location.href = "/admin/products")}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={submitting}
                    >
                      {submitting ? "Submitting..." : "Submit"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Slide>
      </Box>
    </Fade>
  );
};

export default PostProduct;
